/**
 * Viewport view modes: shaded, wireframe, and shaded with a wireframe overlay.
 *
 * Wireframe is Babylon's `scene.forceWireframe` (what the Inspector's
 * "Rendering Mode" sets). The overlay reuses the OutlineRenderer's overlay
 * pass, which draws each mesh again in a flat color with a depth offset; we
 * force that second pass, and only that pass, to wireframe fill. Skinning,
 * morph targets and instances work because the overlay shader handles them.
 */

import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Material } from '@babylonjs/core/Materials/material';
import { SceneComponentConstants } from '@babylonjs/core/sceneComponent';
// Side effect: adds Scene.getOutlineRenderer and Mesh.renderOverlay.
import '@babylonjs/core/Rendering/outlineRenderer';

export type ViewMode = 'shaded' | 'wireframe' | 'overlay';

const OVERLAY_COLOR = new Color3(0.05, 0.05, 0.05);
const OVERLAY_ALPHA = 0.6;

interface SavedMeshState {
    renderOverlay: boolean;
    overlayColor: Color3;
    overlayAlpha: number;
    fillMode: number | null;
}

interface OverlayState {
    enabled: boolean;
    /**
     * Meshes between their before and restore hooks, with what to restore.
     * A render that returns after the before-stage without drawing (no effect
     * yet) leaves its entry here. The mesh's next completed render restores
     * it, and so does leaving overlay mode.
     */
    saved: Map<Mesh, SavedMeshState>;
}

// Each scene's last mode, so setViewMode knows what it is leaving.
const modes = new WeakMap<Scene, ViewMode>();
const overlayStates = new WeakMap<Scene, OverlayState>();

/**
 * Touches only what the mode change needs, so re-applying 'shaded' on scene
 * load adds no hooks and leaves a wireframe set from the Inspector alone.
 */
export function setViewMode(scene: Scene, mode: ViewMode): void {
    const previous = modes.get(scene) ?? 'shaded';
    modes.set(scene, mode);
    if (mode === 'wireframe' || previous === 'wireframe') {
        scene.forceWireframe = mode === 'wireframe';
    }
    const state = overlayStates.get(scene) ?? (mode === 'overlay' ? installOverlay(scene) : undefined);
    if (!state) {
        return;
    }
    state.enabled = mode === 'overlay';
    if (!state.enabled) {
        state.saved.forEach(restore);
        state.saved.clear();
    }
}

function restore(saved: SavedMeshState, mesh: Mesh): void {
    mesh.renderOverlay = saved.renderOverlay;
    mesh.overlayColor = saved.overlayColor;
    mesh.overlayAlpha = saved.overlayAlpha;
    mesh.overrideRenderingFillMode = saved.fillMode;
}

/**
 * Register the per-mesh render hooks once per scene. Stages have no
 * unregister, so the hooks stay and are a no-op while the overlay is off.
 * Order per mesh render: our before-hook → main draw → our fill-mode hook
 * (before the outline step) → overlay draw → our restore hook (after it).
 */
function installOverlay(scene: Scene): OverlayState {
    const state: OverlayState = { enabled: false, saved: new Map() };
    overlayStates.set(scene, state);
    // Steps need an owning component; the hooks are arrows, so `this` is unused.
    const owner = scene.getOutlineRenderer();
    // A mesh disposed while stranded must not stay referenced here.
    scene.onMeshRemovedObservable.add((mesh) => state.saved.delete(mesh as Mesh));
    const outlineStep = SceneComponentConstants.STEP_AFTERRENDERINGMESH_OUTLINE;
    scene._beforeRenderingMeshStage.registerStep(0, owner, (mesh) => {
        // Skyboxes are infinitely far, so their edges only clutter the view.
        if (!state.enabled || mesh.infiniteDistance || state.saved.has(mesh)) {
            return;
        }
        state.saved.set(mesh, {
            renderOverlay: mesh.renderOverlay,
            overlayColor: mesh.overlayColor,
            overlayAlpha: mesh.overlayAlpha,
            fillMode: mesh.overrideRenderingFillMode,
        });
        mesh.renderOverlay = true;
        mesh.overlayColor = OVERLAY_COLOR;
        mesh.overlayAlpha = OVERLAY_ALPHA;
    });
    // Shares priority 0 with the prepass step; any order among those is fine,
    // what matters is running before the outline step.
    scene._afterRenderingMeshStage.registerStep(outlineStep - 1, owner, (mesh) => {
        if (state.saved.has(mesh)) {
            mesh.overrideRenderingFillMode = Material.WireFrameFillMode;
        }
    });
    scene._afterRenderingMeshStage.registerStep(outlineStep + 1, owner, (mesh) => {
        const saved = state.saved.get(mesh);
        if (!saved) {
            return;
        }
        state.saved.delete(mesh);
        restore(saved, mesh);
    });
    return state;
}
